import { Position } from '@xyflow/react'
import {
  PfdData,
  PfdEdge,
  PfdNode,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

export const resolveHighlightColor = (statusRaw?: string): string | null => {
  const value = statusRaw?.toLowerCase()
  if (value === 'critical' || value === 'crit' || value === 'alarm') {
    return '#ef4444'
  }
  if (value === 'warning' || value === 'warn') {
    return '#f59e0b'
  }
  if (value === 'offline' || value === 'down' || value === 'stopped') {
    return '#9ca3af'
  }
  if (value === 'operational' || value === 'ok' || value === 'normal') {
    return '#22c55e'
  }
  return null
}

export const createProcessFlowID = () => {
  return uuidv4().slice(0, 16)
}

export const createProcessFlowEntry = (
  entryId: string,
  processFlowName: string,
  siteID?: string,
  organizationID?: string
): RolodexEntryPutJson | null => {
  return siteID && organizationID
    ? {
        id: entryId,
        type: RolodexEntryType.PROCESS_FLOW_DIAGRAM,
        data: {
          metadata: {
            name: processFlowName,
          },
          diagram: {
            nodes: [],
            edges: [],
          },
        },
        tags: {
          site: siteID,
          organization: organizationID,
        },
      }
    : null
}

export const getRotatedHandleConfig = (
  logicalPosition: 'top' | 'right' | 'bottom' | 'left',
  rotation: number
) => {
  const positionMap = {
    top: {
      0: {
        position: Position.Top,
        style: { top: '0%', left: '50%' },
      },
      90: {
        position: Position.Right,
        style: { top: '0%', right: '50%' },
      },
      180: {
        position: Position.Bottom,
        style: { bottom: '100%', left: '50%' },
      },
      270: {
        position: Position.Left,
        style: { top: '0%', left: '50%' },
      },
    },
    right: {
      0: {
        position: Position.Right,
        style: { top: '50%', right: '0%' },
      },
      90: {
        position: Position.Bottom,
        style: { bottom: '50%', left: '100%' },
      },
      180: {
        position: Position.Left,
        style: { bottom: '50%', left: '100%' },
      },
      270: {
        position: Position.Top,
        style: { top: '50%', left: '100%' },
      },
    },
    bottom: {
      0: {
        position: Position.Bottom,
        style: { bottom: '0%', left: '50%' },
      },
      90: {
        position: Position.Left,
        style: { top: '100%', left: '50%' },
      },
      180: {
        position: Position.Top,
        style: { top: '100%', left: '50%' },
      },
      270: {
        position: Position.Right,
        style: { top: '100%', right: '50%' },
      },
    },
    left: {
      0: {
        position: Position.Left,
        style: { bottom: '50%', left: '0%' },
      },
      90: {
        position: Position.Top,
        style: { top: '50%', left: '0%' },
      },
      180: {
        position: Position.Right,
        style: { top: '50%', right: '100%' },
      },
      270: {
        position: Position.Bottom,
        style: { bottom: '50%', left: '0%' },
      },
    },
  }

  const config =
    positionMap[logicalPosition][rotation as keyof typeof positionMap.top]

  return {
    position: config.position,
    style: config.style,
  }
}

export interface InsertGhostNodeResult {
  newGhostNode: PfdNode
  sourceToGhostEdge: PfdEdge
  ghostToTargetEdge: PfdEdge
  updatedPfd: PfdData
}

export const insertGhostNodeInEdge = (
  selectedEdge: PfdEdge,
  pfd: PfdData,
  position?: { x: number; y: number }
): InsertGhostNodeResult | null => {
  const sourceNode = pfd.nodes.find((node) => node.id === selectedEdge.source)
  const targetNode = pfd.nodes.find((node) => node.id === selectedEdge.target)

  if (!sourceNode || !targetNode) return null

  const ghostPosition = position ?? {
    x: (sourceNode.position.x + targetNode.position.x) / 2,
    y: (sourceNode.position.y + targetNode.position.y) / 2,
  }

  const ghostInHandle = 'left-source'
  const ghostOutHandle = 'right-source'

  const ghostNodeId = `ghost-${Date.now()}`
  const newGhostNode: PfdNode = {
    id: ghostNodeId,
    type: 'ghost',
    position: ghostPosition,
    data: { label: '' },
  }

  const sourceToGhostEdge: PfdEdge = {
    id: `${selectedEdge.source}->${ghostNodeId}`,
    source: selectedEdge.source,
    target: ghostNodeId,
    sourceHandle: selectedEdge.sourceHandle,
    targetHandle: ghostInHandle,
    type: selectedEdge.type,
    data: selectedEdge.data,
    markerEnd: '',
  }

  const ghostToTargetEdge: PfdEdge = {
    id: `${ghostNodeId}->${selectedEdge.target}`,
    source: ghostNodeId,
    target: selectedEdge.target,
    sourceHandle: ghostOutHandle,
    targetHandle: selectedEdge.targetHandle,
    type: selectedEdge.type,
    data: selectedEdge.data,
    markerEnd: selectedEdge.markerEnd,
  }

  const updatedPfd: PfdData = {
    ...pfd,
    nodes: [...pfd.nodes, newGhostNode],
    edges: [
      ...pfd.edges.filter((edge) => edge.id !== selectedEdge.id),
      sourceToGhostEdge,
      ghostToTargetEdge,
    ],
  }

  return {
    newGhostNode,
    sourceToGhostEdge,
    ghostToTargetEdge,
    updatedPfd,
  }
}
